import { Tag } from "@/models/Tag.model";
import { File } from "@/models/File.model";
import { User } from "@/models/User.model";
import { Goal } from "@/models/Goal.model";
import { Comparable } from "@/interfaces/Comparable.interface";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag.ts";
import { FieldValidationError } from "@/stores/errors/FieldValidationError";
import { Model } from "@/stores/AbstractBase.store";

export class LessonPlan implements Model, Comparable {

	static createFromApiData(data: any) {
		const newObject = new LessonPlan();
		newObject.hydrateWithApiData(data);
		return newObject;
	}

	/**
	 * Create a new LessonPlan instance with default values.
	 * (Only used when 'creating' a new lesson plan, not when loading from server)
	 */
	static new() {
		const lessonPlan = new LessonPlan();

		lessonPlan.tags = [];
		lessonPlan.collectionIds = [];
		lessonPlan.supplies = [];
		lessonPlan.attachments = [];
		lessonPlan.goals = [];

		return lessonPlan;
	}

	public id?: string = undefined;
	public originalId: string | undefined = undefined;
	public version: number = 0;
	public latestVersion: number | undefined = undefined;
	public title: string = '';
	public instructions: string = '';

	public isGuided: boolean = false;
	// whether or not this lesson plan is saved in YOUR library
	public isSavedInLib: boolean = false;
	public owner ?: User;
	public creator ?: User;
	public originalCreator ?: User;
	public createdAt: Date | null = null;
	public deletedAt: Date | null = null;
	public canDuplicate: boolean = false;

	public tags ?: Tag[] 			= undefined;
	public collectionIds ?: string[] = undefined;
	public supplies ?: string[] 	= undefined;
	public attachments ?: File[] 	= undefined;
	public goals ?: Goal[] 			= undefined;

	get themeTags(): Tag[] {
		if (!this.tags) {
			return [];
		}
		return this.tags.filter(tag => tag.type === 'theme');
	}

	get activityTags(): Tag[] {
		if (!this.tags) {
			return [];
		}
		return this.tags.filter(tag => tag.type === 'activity');
	}

	set themeTags(tags: Tag[]) {
		tags.forEach(tag => tag.type = 'theme')

		if (!this.tags) {
			this.tags = [];
		}

		this.tags = this.tags.filter(tag => tag.type !== 'theme');
		this.tags.push(...tags);
	}

	set activityTags(tags: Tag[]) {
		tags.forEach(tag => tag.type = 'activity')

		if (!this.tags) {
			this.tags = [];
		}

		this.tags = this.tags.filter(tag => tag.type !== 'activity');
		this.tags.push(...tags);
	}

	get creatorName(): string {
		if (!this.originalCreator) return '';
		// If the original creator is a type of author, return the public name.
		if(this.originalCreator.authorType) {
			return this.originalCreator.getPublicName();
		}
		// If not, return the name of the user, which is safe to show, because if not an author, if must be your own lesson plan or from a (ex-)colleague
		if(this.originalCreator.name) {
			return this.originalCreator.name;
		}
		return '';
	}

	get coCreatorName(): string | null {
		if(this.creator && this.originalCreator && String(this.creator.id) !== String(this.originalCreator.id)) {
			// If the creator is different from the original creator, return the name of the creator as co-author.
			if(this.creator.authorType) {
				return this.creator.getPublicName();
			}
			if(this.creator.name) {
				return this.creator.name;
			}
			return '';
		}
		return null;
	}

	setFromServerData(data: any) {
		console.warn('LessonPlan.setFromServerData is deprecated. Use hydrateWithApiData instead.');
		return this.hydrateWithApiData(data);
	}

	hydrateWithApiData(data: any) {

		this.id = data.id;
		this.version = data.version;
		this.latestVersion = data.latestVersion;
		this.title = data.title;
		this.instructions = data.instructions;

		this.isGuided = data.isGuided;
		this.isSavedInLib = data.inLibrary;
		this.createdAt = new Date(data.createdAt);

		this.tags = [];
		if (typeof (data.tags) !== 'undefined') {
			this.tags = Tag.mapManyFromServer(data.tags);
		}

		this.supplies = [];
		if (typeof (data.supplies) !== 'undefined') {
			this.supplies = data.supplies.map((supply: any) => supply.name);
		}

		this.attachments = [];
		if (typeof data.attachments !== 'undefined') {
			this.attachments = data.attachments.map((fileData: any) => File.mapFromServer(fileData));
		}

		if (typeof data.owner !== 'undefined' && data.owner /* owner may be NULL for old records */) {
			this.owner = User.mapFromServer(data.owner);
		}

		if (typeof data.creator !== 'undefined' && data.creator /* creator may be NULL for old records */) {
			this.creator = User.mapFromServer(data.creator);
		}

		if (typeof data.originalCreator !== 'undefined' && data.originalCreator /* originalCreator may be NULL for old records */) {
			this.originalCreator = User.mapFromServer(data.originalCreator);
		}

		if (typeof data.goals !== 'undefined') {
			this.goals = data.goals.map((goalData: any) => Goal.createFromApiData(goalData));
		}

		if (typeof data.collections !== 'undefined') {
			this.collectionIds = data.collections.map((collection: { id: string }) => collection.id);
		}

		if (typeof data.canDuplicate !== 'undefined') {
			this.canDuplicate = data.canDuplicate;
		}

		if (typeof data.deletedAt !== 'undefined') {
			if (data.deletedAt) {
				this.deletedAt = new Date(data.deletedAt);
			} else {
				this.deletedAt = null;
			}
		}

		return this;
	}

	clone(): LessonPlan {
		let clonedInstance: LessonPlan = Object.assign(
			Object.create(
				Object.getPrototypeOf(this)
			),
			this
		);

		if (this.tags) {
			clonedInstance.tags = [...this.tags];
		}

		if (this.attachments) {
			clonedInstance.attachments = [ ...this.attachments ]
		}

		if (this.goals) {
			clonedInstance.goals = [...this.goals];
		}

		if (this.collectionIds) {
			clonedInstance.collectionIds = [...this.collectionIds];
		}

		return clonedInstance;
	}

	isValid(): boolean {
		return this.title !== null && this.title !== '' && this.title.length > 0;
	}

	validate(): void {
		if (!this.isValid()) {

			const error = new ApiErrorMessageBag();
			error.fields['title'] = new FieldValidationError('title', [ {
				key: 'validation.required',
				parameters: {},
				message: 'Title is required.'
			} ]);

			throw error;

		}
	}

	isLatestVersion(): boolean {
		return this.version === this.latestVersion;
	}

	setFromModel(model: LessonPlan) {
		this.id = model.id;
		this.originalId = model.originalId;
		this.version = model.version;
		this.latestVersion = model.latestVersion;
		this.title = model.title;
		this.instructions = model.instructions;
		this.isGuided = model.isGuided;
		this.isSavedInLib = model.isSavedInLib;
		this.supplies = model.supplies;

		if (typeof model.attachments !== 'undefined') {
			this.attachments = [ ... model.attachments ];
		}

		if (typeof model.goals !== 'undefined') {
			this.goals = [ ... model.goals ];
		}

		if (typeof model.tags !== 'undefined') {
			this.tags = [ ... model.tags ];
		}

		if (typeof model.owner !== 'undefined') {
			this.owner = model.owner;
		}

		if (typeof model.collectionIds !== 'undefined') {
			this.collectionIds = [...model.collectionIds];
		}
	}

	getServerData(): any {
		console.warn('LessonPlan.getServerData is deprecated. Use asApiDataObject instead.');
		return this.asApiDataObject();
	}

	asApiDataObject(): any {
		const out: any = {
			id: this.id,
			originalId: this.originalId,
			version: this.version,
			title: this.title,
			instructions: this.instructions,
			isGuided: this.isGuided,
			inLibrary: this.isSavedInLib,
		};

		if (typeof this.tags !== 'undefined') {
			out.tags = this.tags.map(tag => tag.getServerData());
		}

		if (typeof this.supplies !== 'undefined') {
			out.supplies = [];
			this.supplies.forEach(supply => {
				out.supplies.push({
					name: supply
				});
			});
		}

		if (typeof this.attachments !== 'undefined') {
			out.attachments = this.attachments.map((file: File) => {
				return {
					id: file.id
				}
			});
		}

		if (typeof this.goals !== 'undefined') {
			out.goals = this.goals.map(goal => goal.getLinkData());
		}

		return out;
	}

	public equals(other: Comparable) {
		if (!(other instanceof LessonPlan)) {
			return false;
		}

		return JSON.stringify(this.asApiDataObject()) === JSON.stringify(other.asApiDataObject());
	}

	public isMine(me: User): boolean {
		// Fallback to true if no creator is set (meaning the current user created it)
		if (!this.creator) return true;

		return String(this.creator.id) === String(me.id);
	}
}
